import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import { PageHeader, Table } from '../../../design-system/components/desktop';
import {
  ConsoleCell,
  ConsoleDenied,
  ConsoleEmpty,
  ConsoleError,
  ConsoleStack,
  Notice,
  StatusPill,
} from '../../../design-system/patterns/console';

/**
 * Buyers, the view. Pure.
 *
 * READ THIS BEFORE TRUSTING ANYTHING ON THIS SCREEN
 * -------------------------------------------------
 * THERE IS NO BUYER ENDPOINT. The three rows this page renders come from a
 * hardcoded `mockBuyers` array behind a one second `setTimeout` pretending to be
 * a request. `grep` over `api/modules/` and `api/endpoints/` finds nothing that
 * lists buyers: `admin.module.js` has users, dashboard stats and system
 * settings; `seller.module.js` has sellers; there is no buyers module.
 *
 * The array is NOT removed, because the shape it carries is the shape a real
 * endpoint would fill and deleting it would delete the only description of what
 * this screen is meant to be. What is removed is the pretence. A `Notice` sits
 * above the table saying in words that these rows are placeholder data, so an
 * administrator cannot mistake them for accounts and act on them. The old page
 * shipped them silently, and additionally had a "Buyer Details" overlay that
 * said the feature was under development, which is the same admission made in
 * the least useful possible place: after you click a row.
 *
 * When the endpoint exists, delete the fixture, delete the `Notice`, and this
 * screen becomes the same table as `/admin-users` with a different query.
 */
export function AdminBuyersView({
  buyers = [],
  loading = false,
  error,
  onRetry,
  denied = false,
  placeholderData = false,
}) {
  const columns = [
    {
      key: 'name',
      header: 'Buyer',
      render: (row) => (
        <ConsoleCell title={row.name} sub={row.email} initials={(row.name || 'B').slice(0, 2)} />
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    { key: 'registered', header: 'Registered', muted: true },
  ];

  const shell = (children) => (
    <ConsoleShell
      variant="admin"
      title="Buyers"
      breadcrumbs={[{ label: 'People' }, { label: 'Buyers' }]}
      showSearch={false}
    >
      {children}
    </ConsoleShell>
  );

  if (denied) return shell(<ConsoleDenied />);

  return shell(
    <ConsoleStack>
      <PageHeader title="Buyers" sub="Accounts that buy on the marketplace." />

      {placeholderData ? (
        <Notice title="Placeholder data, not real accounts. ">
          There is no buyer endpoint yet, so the rows below are a fixture describing the shape this
          screen will take. Do not act on them.
        </Notice>
      ) : null}

      <Table
        columns={columns}
        data={error ? [] : buyers}
        rowKey="id"
        loading={loading}
        emptyState={
          error ? (
            <ConsoleError error={error} onRetry={onRetry} />
          ) : (
            <ConsoleEmpty
              /* Not a ledger waiting on other people: there is no buyer
                 endpoint at all, which is a different thing and has its own
                 scenario. It pairs with the Notice above the table. */
              illustration="no-endpoint-yet"
              title="No buyers yet"
              text="Buyer accounts appear here once the endpoint is built."
            />
          )
        }
      />
    </ConsoleStack>,
  );
}

export default AdminBuyersView;
